import { useEffect, useRef } from 'react';
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import multiMonthPlugin from '@fullcalendar/multimonth';
import { STATUS_COLORS, CALENDAR_VIEWS, PENDING_COLORS, BOOKING_STATUSES } from '../constants/statuses';
import { addOneDay } from '../services/dateUtils';

// Helper to get the correct color based on status and initiator
const getBookingColor = (booking) => {
  if (booking.status === BOOKING_STATUSES.PENDING) {
    const initiatedBy = booking.initiated_by || 'host';
    return initiatedBy === 'wwoofer' ? PENDING_COLORS.WWOOFER_INITIATED : PENDING_COLORS.HOST_INITIATED;
  }
  return STATUS_COLORS[booking.status] || '#888';
};

export const useCalendar = (bookings, onEventClick, calendarView = CALENDAR_VIEWS.MONTH, unavailableDates = [], onSelectDates = null, onUnavailableEventClick = null) => {
  const calendarRef = useRef(null);
  const calendarInstanceRef = useRef(null);
  const onEventClickRef = useRef(onEventClick);
  const onSelectDatesRef = useRef(onSelectDates);
  const onUnavailableEventClickRef = useRef(onUnavailableEventClick);
  const savedStateRef = useRef({ view: calendarView, date: null });

  // Update the event click handler ref when it changes
  useEffect(() => {
    onEventClickRef.current = onEventClick;
  }, [onEventClick]);

  useEffect(() => {
    onSelectDatesRef.current = onSelectDates;
  }, [onSelectDates]);

  useEffect(() => {
    onUnavailableEventClickRef.current = onUnavailableEventClick;
  }, [onUnavailableEventClick]);

  useEffect(() => {
    if (!calendarRef.current) return;

    // Only create a calendar if one doesn't exist
    if (!calendarInstanceRef.current) {
      // Prepare initial events from bookings
      const events = bookings.map(booking => ({
        id: booking.id,
        title: `Visit with ${booking.current_user_role === 'host' ? booking.wwoofer_name : booking.host_name}`,
        start: booking.start_date,
        end: addOneDay(booking.end_date),
        backgroundColor: getBookingColor(booking),
        borderColor: getBookingColor(booking),
        extendedProps: { booking }
      }));

      // Prepare background events for unavailable dates
      const unavailableEvents = unavailableDates.map(ud => ({
        id: `unavailable-${ud.id}`,
        start: ud.start_date,
        end: addOneDay(ud.end_date),
        display: 'background',
        backgroundColor: '#ff000033',
        classNames: ['unavailable-date'],
        extendedProps: { unavailableId: ud.id, isUnavailable: true }
      }));

      // Create new calendar instance
      const calendar = new Calendar(calendarRef.current, {
        plugins: [dayGridPlugin, interactionPlugin, multiMonthPlugin],
        initialView: savedStateRef.current.view || calendarView,
        initialDate: savedStateRef.current.date || undefined, // Let FullCalendar use default if no saved date
        events: [...events, ...unavailableEvents],
        selectable: !!onSelectDates,
        selectMirror: true,
        unselectAuto: true,
        select: function (info) {
          if (onSelectDatesRef.current) {
            // end is exclusive in FullCalendar — convert back to inclusive
            const endDate = new Date(info.endStr);
            endDate.setDate(endDate.getDate() - 1);
            const inclusiveEnd = endDate.toISOString().slice(0, 10);
            onSelectDatesRef.current(info.startStr, inclusiveEnd);
            calendar.unselect();
          }
        },
        eventClick: function (info) {
          if (info.event.extendedProps.isUnavailable) {
            if (onUnavailableEventClickRef.current) {
              onUnavailableEventClickRef.current(info.event.extendedProps.unavailableId);
            }
            return;
          }
          if (onEventClickRef.current) {
            // Pass booking ID instead of booking object to ensure fresh data is used
            onEventClickRef.current(info.event.extendedProps.booking.id);
          }
        },
        height: calendarView === CALENDAR_VIEWS.YEAR ? 800 : calendarView === CALENDAR_VIEWS.MULTI_MONTH_STACK ? 900 : 650,
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: '',//calendarView === CALENDAR_VIEWS.YEAR || calendarView === CALENDAR_VIEWS.MULTI_MONTH_STACK ? '' : 'dayGridMonth'
        },
        eventDisplay: 'block',
        dayMaxEvents: calendarView === CALENDAR_VIEWS.YEAR ? 2 : calendarView === CALENDAR_VIEWS.MULTI_MONTH_STACK ? 1 : 3,
        views: {
          multiMonthYear: {
            type: 'multiMonth',
            duration: { years: 1 },
            multiMonthMaxColumns: 3,
            multiMonthMinWidth: 250,
          },
          multiMonthStack: {
            type: 'multiMonth',
            duration: { months: 6 },
            multiMonthMaxColumns: 1,
            multiMonthMinWidth: 350,
          }
        },
        datesSet: function (info) {
          // Save the current view state whenever the user navigates or the view changes
          savedStateRef.current = {
            view: info.view.type,
            date: info.view.currentStart
          };
        }
      });

      calendar.render();
      calendarInstanceRef.current = calendar;
    }

    // Cleanup function
    return () => {
      if (calendarInstanceRef.current) {
        // Save state before destroying
        savedStateRef.current = {
          view: calendarInstanceRef.current.view.type,
          date: calendarInstanceRef.current.getDate()
        };
        calendarInstanceRef.current.destroy();
        calendarInstanceRef.current = null;
      }
    };
  }, [calendarView]); // Only recreate when calendarView changes

  // Separate effect to handle booking updates
  useEffect(() => {
    if (calendarInstanceRef.current) {
      // Save current view state before updating events
      savedStateRef.current = {
        view: calendarInstanceRef.current.view.type,
        date: calendarInstanceRef.current.getDate()
      };

      // Prepare events from bookings
      const events = bookings.map(booking => ({
        id: booking.id,
        title: `Visit with ${booking.current_user_role === 'host' ? booking.wwoofer_name : booking.host_name}`,
        start: booking.start_date,
        end: addOneDay(booking.end_date),
        backgroundColor: getBookingColor(booking),
        borderColor: getBookingColor(booking),
        extendedProps: { booking }
      }));

      // Get current events to compare
      const currentEvents = calendarInstanceRef.current.getEvents();

      // Remove booking events that no longer exist
      currentEvents.forEach(event => {
        if (!event.extendedProps.isUnavailable && !events.find(newEvent => newEvent.id == event.id)) {
          event.remove();
        }
      });

      // Add or update booking events
      events.forEach(newEvent => {
        const existingEvent = currentEvents.find(event => event.id == newEvent.id);
        if (existingEvent) {
          // Update existing event properties if they've changed
          if (existingEvent.title !== newEvent.title ||
            existingEvent.startStr !== newEvent.start ||
            existingEvent.endStr !== newEvent.end ||
            existingEvent.backgroundColor !== newEvent.backgroundColor) {
            existingEvent.setProp('title', newEvent.title);
            existingEvent.setStart(newEvent.start);
            existingEvent.setEnd(newEvent.end);
            existingEvent.setProp('backgroundColor', newEvent.backgroundColor);
            existingEvent.setProp('borderColor', newEvent.borderColor);
            existingEvent.setExtendedProp('booking', newEvent.extendedProps.booking);
          }
        } else {
          // Add new event
          calendarInstanceRef.current.addEvent(newEvent);
        }
      });
    }
  }, [bookings]); // Update events when bookings change

  // Sync unavailable background events when unavailableDates changes
  useEffect(() => {
    if (!calendarInstanceRef.current) return;

    const currentEvents = calendarInstanceRef.current.getEvents();

    // Remove stale unavailability background events
    currentEvents.forEach(event => {
      if (event.extendedProps.isUnavailable) {
        event.remove();
      }
    });

    // Add current unavailable date ranges as background events
    unavailableDates.forEach(ud => {
      calendarInstanceRef.current.addEvent({
        id: `unavailable-${ud.id}`,
        start: ud.start_date,
        end: addOneDay(ud.end_date),
        display: 'background',
        backgroundColor: '#ff000033',
        classNames: ['unavailable-date'],
        extendedProps: { unavailableId: ud.id, isUnavailable: true }
      });
    });
  }, [unavailableDates]);

  // Handle calendar view changes from props
  useEffect(() => {
    if (calendarInstanceRef.current && calendarView !== savedStateRef.current.view) {
      // Save current date before changing view
      const currentDate = calendarInstanceRef.current.getDate();
      calendarInstanceRef.current.changeView(calendarView, currentDate);
      savedStateRef.current = {
        view: calendarView,
        date: currentDate
      };
    }
  }, [calendarView]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (calendarInstanceRef.current) {
        calendarInstanceRef.current.destroy();
      }
    };
  }, []);

  return calendarRef;
};
